import { useEffect, useRef, type ReactNode } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { DmSearchState } from '../../../../../hooks/usePracticeDm';
import { DmInlineError } from './DmInlineError';
import { dmErrorMessage } from './dmErrorMessage';

const SEARCH_MAX_CHARS = 100;

interface DmSearchListProps<T> {
    search: string;
    onSearchChange: (search: string) => void;
    state: DmSearchState<T>;
    placeholder: string;
    emptyLabel: string;
    renderItem: (item: T) => ReactNode;
}

function SearchStatus<T>({
    state,
    emptyLabel
}: Pick<DmSearchListProps<T>, 'state' | 'emptyLabel'>) {
    if (!state.isActive)
        return <p className="text-[13px] text-gray-400">Type at least 2 characters.</p>;
    if (state.isSearching) return <p className="text-[13px] text-gray-500">Searching DM…</p>;
    if (state.error)
        return <DmInlineError message={dmErrorMessage(state.error, 'DM search failed.')} />;
    if (state.results.length === 0)
        return <p className="text-[13px] text-gray-500">{emptyLabel}</p>;
    return null;
}

export function DmSearchList<T>({
    search,
    onSearchChange,
    state,
    placeholder,
    emptyLabel,
    renderItem
}: DmSearchListProps<T>) {
    const inputRef = useRef<HTMLInputElement>(null);
    // Selecting the prefilled term lets the first keystroke replace it.
    useEffect(() => inputRef.current?.select(), []);
    const showResults = state.isActive && !state.isSearching && !state.error;
    return (
        <div className="flex flex-col gap-2">
            <span className="h-10 border border-gray-300 rounded-lg px-3 flex items-center gap-2 bg-white focus-within:border-gray-800 focus-within:ring-2 focus-within:ring-gray-200">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                    ref={inputRef}
                    autoFocus
                    value={search}
                    maxLength={SEARCH_MAX_CHARS}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    className="flex-1 min-w-0 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
                />
            </span>
            <SearchStatus state={state} emptyLabel={emptyLabel} />
            {showResults && state.results.length > 0 && (
                <OverlayScrollArea contentClassName="max-h-64" trackInsetPx={4}>
                    <div className="flex flex-col gap-1.5 pr-2">
                        {state.results.map(renderItem)}
                    </div>
                </OverlayScrollArea>
            )}
        </div>
    );
}
